import { EventEmitter, Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from "@angular/forms";
import {filter, map, Observable, Subject} from 'rxjs';

import { FormPage } from "../../shared/classes/form-page";
import { IConfigurationSection } from './form.interface';
import { ConfigurationType } from '../../shared/enums/ConfigurationType';
import { Form, FormNoData } from '../../shared/interfaces/database/formularios';
import { DynamicForm } from '../../shared/classes/dynamic-form';
import { BaseControl, ControlType, TypeChecker } from '../../shared';

export interface IFormOptions {
	isDesigner: boolean
	enableSubmit: boolean
	theme?: string
	viewAnswerMode?: boolean
}

export enum IFormEventType {
  formLoaded = 'form_loaded',
  subFormLoaded = 'sub_form_loaded',
  remove = 'remove',
  add = 'add',
}

export interface IFormEvent{
  eventType: IFormEventType; // tipo de evento Ej: 'remove', 'add', 'update', etc (depende del tipo del control)
  form : DynamicForm; // el formulario actual
  control ?: BaseControl<any>; // el control que mando el evento
  data ?: any //data asociado al evento (depende del tipo del control)
}

@Injectable({
	providedIn: 'root'
})
export class FormCreatorService implements IFormOptions {

	private _form !: DynamicForm;
	private _loadedForm !: FormNoData;
  private _radicado ?: string; // esto se debe cargar para descargar archivos asociados al formulario
	private _theme?: string;
	private _viewAnswerMode?: boolean;

	isDesigner !: boolean;
	enableSubmit !: boolean;

  activeIndex: number = 0;

	private _configurationSection: IConfigurationSection
	private _subformulariosToLoad: Map<string, any[]> = new Map<string, any[]>();

	showConfigurationEvent = new Subject<void>();
	downloadFileEvent = new EventEmitter<[string, string, string, boolean]>();
  formEventSubject = new Subject<IFormEvent>();

  formEventNewSubFormSubject = new Subject<any>();
  eventSubjectClearForm = new Subject<void>();

  //drag and drop fields
  dragStartControl !: BaseControl<any> | null;
  dragStartPage !: FormPage | null;

	constructor() {
		this._form = new DynamicForm({});
		this._configurationSection = { show: true, type: ConfigurationType.Global }
	}

	//#region Temp Fields
	/*
	*Esta informacion es para llenar los campos del usuario en el portal
	*/
	private _userData?: any;
	get userData() {
		return this._userData;
	}

	set userData(value: any) {
		this._userData = value;
	}
	//#endregion Temp Fields

	//#region Getters and Setters
	get Form(): DynamicForm {
		return this._form;
	}

	get Pages(): FormPage[] {
		return this._form.pages;
	}

	get FormGroup(): FormGroup {
		return this._form.formGroup;
	}

	get ConfigurationSection() {
		return this._configurationSection;
	}

	get LoadedForm() {
		return this._loadedForm;
	}

	set LoadedForm(form: FormNoData) {
		this._loadedForm = form;
	}

	get theme() {
		return this._theme ?? 'default';
	}

	set theme(value: string) {
		this._theme = value;
	}

	get viewAnswerMode() {
		return this._viewAnswerMode ?? false;
	}

	set viewAnswerMode(value: boolean) {
		this._viewAnswerMode = value
	}

  get activePage(): FormPage | undefined {
    if(this.Pages.length == 0) return undefined;
    return this.Pages[this.activeIndex];
  }

  get pagesFormGroup() {
    return this.FormGroup.get('pages');
  }

  get radicado(): string | undefined {
    return this._radicado;
  }

  set radicado(value: string | undefined) {
    this._radicado = value;
  }

  getPageFormGroup(pageName: string): FormGroup {
    const control = this.pagesFormGroup?.get(pageName);
    if (control instanceof FormGroup) {
      return control;
    }
    throw new Error('Expected FormGroup');
  }

	getSubformulariosToLoad(controlName: string): any {
		return this._subformulariosToLoad.get(controlName);
	}

  setOptions(options: IFormOptions) {
		this.isDesigner = options.isDesigner;
		this.enableSubmit = options.enableSubmit
		this._viewAnswerMode = options.viewAnswerMode ?? false;
	}
	//#endregion Getters and Setters

	//#region Form creation/loading
	initializeNewForm() {
		this._form = new DynamicForm({ title: "New Form" });
		this._form.createNewPage();
	}

	loadForm(form: Form, radicado ?: string) {
		const { contenido, ...formWithoutData } = form;
    this.radicado = radicado;
		this._loadedForm = formWithoutData as FormNoData;
		this._form = new DynamicForm(JSON.parse(contenido), this.viewAnswerMode);
    this.emitFormEvent({
      eventType: IFormEventType.formLoaded,
      form: this._form
    });
	}

	loadRespuesta(respuesta: string) {
		let res = JSON.parse(respuesta);
		const pages = this.FormGroup.get("pages") as FormGroup;
		Object.keys(pages.controls).forEach(pageName => {
			const page = pages.get(pageName) as FormGroup;
			Object.keys(page.controls).forEach(controlName => {
				const formControl = page.get(controlName);
				const control = this.Form.findControlByName(controlName);

				let value = res[pageName][controlName];
				//heres goes special logic for loading control values
				switch (control?.type) {
					case ControlType.Calendar: {
						value = new Date(value);
						break;
					}
					case ControlType.FileUpload: {
						if (TypeChecker.isFileUpload(control)) {
							control.downloadFileMode = true;
						}
						break;
					}
					case ControlType.SubFormulario: {
						if (TypeChecker.isSubFormulario(control)) {
							this._subformulariosToLoad.set(control.name, value);
						}
						break;
					}
					default:
				}

				formControl?.patchValue(value);
			});
		});

	}
	//#endregion Form creation/loading

	//#region Validations
	validateUniquePageName(oldPageName: string, newPageName: string): boolean {
		if (oldPageName === newPageName)
			return true;
		const foundpage = this.Form.findPageByName(newPageName)
		if (foundpage)
			return false;
		return true;
	}

	validateUniqueControlName(oldControlName: string, newControlName: string): boolean {
		if (oldControlName === newControlName)
			return true;
		const foundControl = this.Form.findControlByName(newControlName)
		if (foundControl)
			return false;
		return true;
	}

	getValidationError(control?: AbstractControl | null): string {
		let errorMessage = '';
		if (!control) return errorMessage;
		const formErrors = control?.errors;
		if (!formErrors) return errorMessage;

		// Loop through each error key and handle them individually
		Object.keys(formErrors).forEach(errorKey => {
			switch (errorKey) {
				case 'required':
					errorMessage = `"Campo es requerido.`;
					break;
				case 'maxlength':
					errorMessage = `Máximo ${formErrors['maxlength']?.requiredLength} caracteres permitidos.`;
					break;
				case 'uniqueName':
					errorMessage = 'El nombre debe ser único.';
					break;
				default:
					errorMessage = 'Valor inválido.';
					break;
			}
		});
		return errorMessage;
	}
	//#endregion Validations

	//#region Events
	emitShowConfigurationEvent(configuration: IConfigurationSection, element: HTMLElement) {
		this._configurationSection.selectedElement?.classList.remove("selected-panel")
		this._configurationSection = configuration;
		this._configurationSection.selectedElement = element;
		this._configurationSection.selectedElement?.classList.add("selected-panel")

		this.showConfigurationEvent.next();
	}

	emitDownloadFileEvent(fileId: string, fileName: string, saveFile: boolean = false) {
    if(!this.radicado) {
      throw new Error("Radicado no definido para descargar el archivo.");
    }
		this.downloadFileEvent.emit([this.radicado, fileId, fileName, saveFile]);
	}

	emitDownloadSubformFileEvent(radicado: string, fileId: string, fileName: string, saveFile: boolean = false) {
    if(!this._radicado) {
      throw new Error("Radicado no definido para descargar el archivo.");
    }
		this.downloadFileEvent.emit([radicado, fileId, fileName, saveFile]);
	}

  emitFormEvent(data: IFormEvent){
    //To ensure everything is loaded before emitting the event
    setTimeout(() => {
      this.formEventSubject.next(data);
    });
  }

	//#endregion Events

  //#region Drag and drop

  clearDragData(): void {
    this.dragStartControl = null;
    this.dragStartPage = null;
  }

  moveControl(controlName: string, moveToControlName: string, orientation: 'left' | 'right'): void {
    const controlResult = this.Form.findControlIndex(controlName);
    const moveToControlResult = this.Form.findControlIndex(moveToControlName);
    if (!controlResult || !moveToControlResult) return;

    const controlPage = this.Pages[controlResult.pageIndex];
    const moveToControlPage = this.Pages[moveToControlResult.pageIndex];
    let moveToControlIndex = moveToControlResult.controlIndex;

    // Remove the control from its current position
    const [movedControl] = controlPage.controls.splice(controlResult.controlIndex, 1);

    // If on the same page, recalculate the target index after removal
    if (controlPage === moveToControlPage && controlResult.controlIndex < moveToControlIndex) {
      moveToControlIndex--;
    }

    const insertIndex = orientation === 'left' ? moveToControlIndex : moveToControlIndex + 1;
    moveToControlPage.controls.splice(insertIndex, 0, movedControl);

    if (controlPage !== moveToControlPage) {
      // if the page changes then we need to update the form groups
      controlPage.formGroup.removeControl(movedControl.name);
      moveToControlPage.formGroup.addControl(movedControl.name, movedControl.FormControl);

      moveToControlPage.intializeControlRows();
    }
    controlPage.intializeControlRows();
  }

  //#endregion Change control location

  getFormAsString(): string {
		return JSON.stringify(this.Form.toJSON());
	}

  printFormValues() {
		console.log("Form Group")
		console.log(this.Form.formGroup.value);
		console.log("Form")
		const json = this.Form.toJSON();
		console.log(json);
	}

  clearCurrentPage(){
    if(!this.activePage) return;

    this.Form.clearPageValues(this.activePage.name);
    this.eventSubjectClearForm.next();

  }

  emitSubFormEventAdd(data?: any): Observable<any> {
    this.formEventNewSubFormSubject.next(data);
    return this.formEventSubject
      .pipe(filter(subFormEvent => subFormEvent.eventType == IFormEventType.add));
  }

  public resetForm(){
    this._form = new DynamicForm({});
  }
}
